import React, { useState } from 'react';
import {
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import { CreditCard as CreditCardIcon } from '@mui/icons-material';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontSmoothing: 'antialiased',
    },
    invalid: {
      color: '#9e2146',
    },
  },
  hidePostalCode: true, // Hide postal code to avoid validation errors
};

const StripePaymentForm = ({ 
  clientSecret, 
  amount, 
  currency = 'gbp',
  onSuccess, 
  onError,
  loading: externalLoading = false 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [completed, setCompleted] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setError('Stripe has not loaded yet. Please try again.');
      return;
    }

    if (!clientSecret) {
      setError('Payment information is incomplete. Please try again.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card information is incomplete.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: 'Test User', // You can make this dynamic later
          },
        },
      });

      if (error) {
        console.error('Payment confirmation error:', error);
        setError(error.message);
        if (onError) onError(error);
      } else if (paymentIntent) {
        console.log('Payment confirmed:', paymentIntent);
        setCompleted(true);
        if (onSuccess) onSuccess(paymentIntent);
      } else {
        setError('Payment confirmation failed - no payment intent returned');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('An unexpected error occurred. Please try again.');
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount, currency) => {
    const symbol = currency === 'gbp' ? '£' : '$';
    return `${symbol}${(amount / 100).toFixed(2)}`;
  };

  if (completed) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
        <Typography variant="h6" gutterBottom>
          ✅ Payment Successful!
        </Typography>
        <Typography variant="body2">
          Your payment of {formatAmount(amount, currency)} has been authorized.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 2 }} variant="outlined">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CreditCardIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">
            Payment Details
          </Typography>
        </Box>
        
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Amount: {formatAmount(amount, currency)}
        </Typography>

        <Box sx={{ 
          p: 2, 
          border: '1px solid #e0e0e0', 
          borderRadius: 1, 
          backgroundColor: '#fafafa',
          mb: 2
        }}>
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </Box>

        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
          💡 <strong>Test Mode:</strong> Use card number 4242 4242 4242 4242, any future expiry date, and any 3-digit CVC.
        </Typography>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={!stripe || loading || externalLoading}
          sx={{ mt: 2, py: 1.5 }}
        >
          {loading || externalLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Processing...
            </Box>
          ) : (
            `Pay ${formatAmount(amount, currency)}`
          )}
        </Button>
      </Paper>
    </Box>
  );
};

export default StripePaymentForm;